/**
 * The thread's scroll box: day separators, the new-messages marker, the messages, the typing row,
 * and „Najnowsze" when the trainer has scrolled away from the bottom.
 *
 * ── Where the box stands ─────────────────────────────────────────────────────────────────────
 * - Opening a conversation lands on its newest message, with no animation.
 * - A message arriving while the trainer reads the bottom is scrolled into view; arriving while
 *   they read further up, it is counted on „Najnowsze" instead of moving what they are reading.
 * - Their own send always scrolls to it (`scrollToEndSignal`).
 * - Reaching the top reads the previous page, and the box is held where it was: the rows that
 *   arrive above push the content down by exactly their height, which is added back to the scroll.
 *
 * Only a message that arrived while the thread was on screen plays the canon's rise; a thread that
 * opens does not animate forty bubbles in.
 */

import { useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChatMessage } from '../model/chatMessage';
import type { ThreadItem } from '../model/threadItems';

import { ArrowDownGlyph } from './ChatGlyphs';
import { MessageBubble, type MediaView } from './MessageBubble';
import styles from './chat.module.css';

const AT_END_SLACK = 60;
const LOAD_OLDER_AT = 120;

interface MessageThreadProps {
  readonly conversationId: number;
  readonly items: readonly ThreadItem[];
  readonly typingName: string | null;
  readonly hasOlder: boolean;
  readonly loadingOlder: boolean;
  readonly onLoadOlder: () => void;
  readonly customerId: number | null;
  readonly theirName: string;
  readonly dividerLeaving: boolean;
  readonly scrollToEndSignal: number;
  readonly onOpenMedia: (view: MediaView) => void;
  readonly onOpenClip: (clipId: number) => void;
  readonly onRetry: (clientId: string) => void;
  readonly onRemove: (clientId: string) => void;
  readonly onDragOver: (event: DragEvent) => void;
}

function lastMessage(items: readonly ThreadItem[]): ChatMessage | null {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item?.type === 'message') return item.message;
  }
  return null;
}

export function MessageThread({
  conversationId,
  items,
  typingName,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  customerId,
  theirName,
  dividerLeaving,
  scrollToEndSignal,
  onOpenMedia,
  onOpenClip,
  onRetry,
  onRemove,
  onDragOver,
}: MessageThreadProps) {
  const { t, i18n } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = useState(true);
  const [missed, setMissed] = useState(0);
  const atEndRef = useRef(true);
  const [opened, setOpened] = useState(() => ({
    conversationId,
    keys: new Set(items.map((item) => item.key)),
  }));
  const lastKey = useRef<string | null>(null);
  const firstKey = useRef<string | null>(null);
  const heightBefore = useRef<number | null>(null);

  // The keys on screen when the conversation opened never animate; everything after does. A thread
  // whose first page is still on its way opens with none, so the page that lands is taken whole.
  if (opened.conversationId !== conversationId || (opened.keys.size === 0 && items.length > 0)) {
    setOpened({ conversationId, keys: new Set(items.map((item) => item.key)) });
  }

  const scrollToEnd = (smooth: boolean) => {
    const element = box.current;
    if (element === null) return;
    element.scrollTo({ top: element.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  useLayoutEffect(() => {
    const element = box.current;
    if (element === null) return;
    const newest = lastMessage(items);
    const first = items[0]?.key ?? null;

    // Rows prepended by an older page: hold the reading position.
    if (heightBefore.current !== null && first !== firstKey.current && lastKey.current === (newest?.key ?? null)) {
      element.scrollTop += element.scrollHeight - heightBefore.current;
      heightBefore.current = null;
    } else if (lastKey.current === null && newest !== null) {
      scrollToEnd(false);
    } else if (newest !== null && newest.key !== lastKey.current) {
      if (atEndRef.current || newest.mine) scrollToEnd(true);
      else setMissed((count) => count + 1);
    }
    lastKey.current = newest?.key ?? null;
    firstKey.current = first;
  }, [items]);

  // The trainer's own send: whatever they had missed is now below what they are about to see.
  const [signalSeen, setSignalSeen] = useState(scrollToEndSignal);
  if (signalSeen !== scrollToEndSignal) {
    setSignalSeen(scrollToEndSignal);
    setMissed(0);
  }

  useEffect(() => {
    if (scrollToEndSignal === 0) return;
    scrollToEnd(true);
  }, [scrollToEndSignal]);

  useEffect(() => {
    if (typingName !== null && atEndRef.current) scrollToEnd(true);
  }, [typingName]);

  const onScroll = () => {
    const element = box.current;
    if (element === null) return;
    const end = element.scrollHeight - element.scrollTop - element.clientHeight < AT_END_SLACK;
    if (end !== atEndRef.current) {
      atEndRef.current = end;
      setAtEnd(end);
      if (end) setMissed(0);
    }
    if (element.scrollTop < LOAD_OLDER_AT && hasOlder && !loadingOlder) {
      heightBefore.current = element.scrollHeight;
      onLoadOlder();
    }
  };

  const dayLabel = (item: Extract<ThreadItem, { type: 'day' }>) => {
    if (item.label.kind === 'today') return t('chat.date.today');
    if (item.label.kind === 'yesterday') return t('chat.date.yesterday');
    return new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: 'numeric', month: 'long' }).format(
      new Date(item.label.at),
    );
  };

  return (
    <>
      <div
        ref={box}
        className={styles.thread}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={t('web:chat.thread.label')}
        onScroll={onScroll}
        onDragOver={onDragOver}
      >
        {loadingOlder ? <div className={styles.olderNote}>{t('web:chat.thread.loadingOlder')}</div> : null}
        {items.map((item) => {
          if (item.type === 'day') {
            return (
              <div className={styles.day} role="separator" key={item.key}>
                <span>{dayLabel(item)}</span>
              </div>
            );
          }
          if (item.type === 'divider') {
            const since = new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(
              new Date(item.since),
            );
            const words = t('web:chat.thread.newMessages', { count: item.count });
            return (
              <div
                className={`${styles.divider} ${dividerLeaving ? styles.dividerLeaving : ''}`}
                role="separator"
                aria-label={`${words}, ${t('web:chat.thread.since', { time: since })}`}
                key={item.key}
              >
                <span className={styles.dividerLine} />
                <span className={styles.dividerLabel}>
                  <i className={styles.dividerDot} />
                  <span className={styles.dividerWords}>{words}</span>
                  <span className={styles.dividerSince}>{t('web:chat.thread.since', { time: since })}</span>
                </span>
                <span className={`${styles.dividerLine} ${styles.dividerLineEnd}`} />
              </div>
            );
          }
          return (
            <MessageBubble
              key={item.key}
              message={item.message}
              first={item.first}
              last={item.last}
              entering={!opened.keys.has(item.key)}
              customerId={customerId}
              theirName={theirName}
              onOpenMedia={onOpenMedia}
              onOpenClip={onOpenClip}
              onRetry={onRetry}
              onRemove={onRemove}
            />
          );
        })}
        {typingName !== null ? (
          <div className={`${styles.msg} ${styles.theirs} ${styles.first} ${styles.last} ${styles.typingRow}`}>
            <div className={styles.body}>
              <div className={`${styles.bubble} ${styles.typing}`} role="img" aria-label={t('chat.window.typing', { name: typingName })}>
                <i className={styles.typingDot} />
                <i className={styles.typingDot} />
                <i className={styles.typingDot} />
              </div>
              <div className={styles.typingLabel}>{t('chat.window.typing', { name: typingName })}</div>
            </div>
          </div>
        ) : null}
      </div>
      {atEnd ? null : (
        <button
          className={styles.jump}
          type="button"
          onClick={() => {
            scrollToEnd(true);
            setMissed(0);
          }}
        >
          <ArrowDownGlyph />
          {t('web:chat.thread.latest')}
          {missed > 0 ? <span className={`${styles.badge} ${styles.num}`}>{missed}</span> : null}
        </button>
      )}
    </>
  );
}
